// PromptProvider — inline replacement for window.prompt() / window.confirm().
//
// Chromium/Electron 27+ removed window.prompt(): it throws
// `Error: prompt() is and will not be supported` on call, aborting the whole
// handler before the follow-on API call.  This context provides a Promise-
// based alternative that renders an inline modal:
//
//   const ask = useAsk()
//   const value = await ask({ message: 'Enter reason:', defaultValue: '' })
//   if (value === null) return   // user cancelled
//
// For yes/no confirmation use ask({ type: 'confirm', message: '...' }) — the
// promise resolves to `true` / `false` / `null` (cancel).
//
// The modal is registered once at the app root; every consumer shares it.

import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'

const PromptContext = createContext(null)

export function PromptProvider ({ children }) {
  const [state, setState] = useState(null)
  const inputRef = useRef(null)

  const ask = useCallback((opts = {}) => {
    const {
      message = '',
      title = '',
      defaultValue = '',
      placeholder = '',
      type = 'text',           // 'text' | 'textarea' | 'confirm' | 'password' | 'number'
      confirmLabel = 'OK',
      cancelLabel = 'Cancel',
      maxLength = 4000,
      required = false,
      danger = false,          // red confirm button (for destructive actions)
    } = opts
    return new Promise((resolve) => {
      setState({
        message, title, defaultValue, placeholder, type,
        confirmLabel, cancelLabel, maxLength, required, danger,
        value: defaultValue,
        resolve
      })
    })
  }, [])

  const close = (result) => {
    if (state?.resolve) state.resolve(result)
    setState(null)
  }

  const onConfirm = () => {
    if (!state) return
    if (state.type === 'confirm') return close(true)
    const value = String(state.value ?? '')
    if (state.required && !value.trim()) return   // block empty submit
    close(value)
  }

  // ESC + Enter keyboard handling + autofocus
  useEffect(() => {
    if (!state) return
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(state.type === 'confirm' ? false : null) }
      // Enter to confirm — but not inside textarea (Shift+Enter still adds newline)
      if (e.key === 'Enter' && state.type !== 'textarea') {
        e.preventDefault(); onConfirm()
      }
    }
    window.addEventListener('keydown', onKey)
    setTimeout(() => inputRef.current?.focus(), 30)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  return (
    <PromptContext.Provider value={ask}>
      {children}
      {state && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onMouseDown={(e) => { if (e.target === e.currentTarget) close(state.type === 'confirm' ? false : null) }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5"
            role="dialog" aria-modal="true">
            {state.title && (
              <h3 className="text-base font-semibold text-gray-800 mb-2">{state.title}</h3>
            )}
            {state.message && (
              <div className="text-sm text-gray-700 whitespace-pre-wrap mb-3">{state.message}</div>
            )}
            {state.type !== 'confirm' && (
              state.type === 'textarea' ? (
                <textarea
                  ref={inputRef}
                  value={state.value}
                  onChange={(e) => setState(s => ({ ...s, value: e.target.value }))}
                  placeholder={state.placeholder}
                  rows={4}
                  maxLength={state.maxLength}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              ) : (
                <input
                  ref={inputRef}
                  type={state.type === 'password' ? 'password' : state.type === 'number' ? 'number' : 'text'}
                  value={state.value}
                  onChange={(e) => setState(s => ({ ...s, value: e.target.value }))}
                  placeholder={state.placeholder}
                  maxLength={state.maxLength}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              )
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => close(state.type === 'confirm' ? false : null)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">
                {state.cancelLabel}
              </button>
              <button
                onClick={onConfirm}
                disabled={state.type !== 'confirm' && state.required && !String(state.value || '').trim()}
                className={`px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50 ${
                  state.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-primary-600 hover:bg-primary-700'
                }`}>
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </PromptContext.Provider>
  )
}

export function useAsk () {
  const ctx = useContext(PromptContext)
  if (!ctx) {
    // Fail-soft: if a component tree renders outside the provider (rare —
    // usually only in tests), fall back to the throw-anyway browser prompt.
    // In production it's always inside the provider.
    if (typeof console !== 'undefined') console.warn('useAsk: no PromptProvider in tree')
    return async () => null
  }
  return ctx
}
